import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import snakeCase from 'lodash/snakeCase';
import RelatedProductsCollection
  from '@fuww/library/src/RelatedProducts';
import { RelatedProductCard }
  from '@fuww/library/src/RelatedProducts/RelatedProductCard';
import { PortraitImageContainer } from '@fuww/library/src/Card';
import { ApolloClient, useApolloClient } from '@apollo/client';
import createAnalyticsEvent
  from '../../lib/mutations/createAnalyticsEvent.graphql';
import { useSiteContext } from '../SiteContext';
import Image from '../Image';
import Routes from '../../routes.mjs';
import withQuery from '../../lib/withQuery';
import { getTargetOrigin } from '../../lib/redirectRouteToDomain';

const getProductEventTracker = (
  client: ApolloClient<unknown>,
  id: number,
  payload: string,
) => async () => {
  await client.mutate({
    mutation: createAnalyticsEvent,
    variables: {
      title: 'Related products click',
      type: 'CLICK',
      subjectType: 'Product',
      subjectId: String(id),
      payload,
    },
  });
  return true;
};

const relatedProductsPropertyTypes = {
  data: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({
      brand: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
      id: PropTypes.number.isRequired,
      imageUrls: PropTypes.arrayOf(PropTypes.string.isRequired),
      name: PropTypes.string.isRequired,
      sourceName: PropTypes.string.isRequired,
      url: PropTypes.string,
    }).isRequired).isRequired,
  }).isRequired,
  articleId: PropTypes.number,
  callToActionMessage: PropTypes.string,
  headerText: PropTypes.string.isRequired,
  isFashionFinder: PropTypes.bool,
  variables: PropTypes.shape({
    marketId: PropTypes.number,
  }).isRequired,
};

type RelatedProductsProperties = InferProps<
  typeof relatedProductsPropertyTypes
>;

export const RelatedProducts = ({
  data,
  articleId,
  callToActionMessage,
  headerText,
  isFashionFinder,
  variables,
}: RelatedProductsProperties) => {
  const client = useApolloClient();
  const { origin, newsboard } = useSiteContext();
  const { products } = data;
  const { marketId } = variables;

  return (
    <RelatedProductsCollection
      title={headerText}
      products={products.map((product) => {
        const {
          brand: {
            id: brandId, name: brandName,
          }, id, imageUrls, name, sourceName, url,
        } = product;

        const productUrl = url && isFashionFinder
          ? url
          : `${getTargetOrigin(origin)}${Routes.findAndGetUrls(
            'product', { id: id.toString() }).urls.as
          }`;

        const productEventTracker = getProductEventTracker(
          client,
          id,
          JSON.stringify({
            id,
            name,
            sourceName,
            brandId,
            brandName,
            marketId,
            newsboard,
            articleId,
          }),
        );

        return (
          <RelatedProductCard
            key={id}
            graphic={
              imageUrls
                && (
                  <PortraitImageContainer>
                    <Image
                      placeholder={imageUrls[0]}
                      src={imageUrls[1]}
                      srcSet={`${imageUrls[1]
                      } 138w, ${imageUrls[2]
                      } 276w, ${imageUrls[3]
                      } 552w`}
                      alt={name}
                      width="138px"
                      height="184px"
                      sizes="138px"
                    />
                  </PortraitImageContainer>
                )
            }
            name={brandName}
            url={productUrl}
            callToActionMessage={callToActionMessage}
            productEventTracker={productEventTracker}
            analyticsClassNames={
              [
                `brand-${snakeCase(brandName)}`,
                `retailer-${snakeCase(sourceName)}`,
              ]
            }
          />
        );
      })}
    />
  );
};

RelatedProducts.propTypes = relatedProductsPropertyTypes;

export default withQuery(RelatedProducts);
